import { useEventsStore } from "@/stores/apps/events"
import { axiosToken } from "@/utils/http"
import type { AxiosResponse } from "axios"
import { useRouter } from "vue-router"
import i18n from "@/utils/i18n"

type EventContentDownloadProgress = {
    total_files: number
    processed_files: number
    remaining_files: number
    status: string
    cached?: boolean
    download_url?: string
}

type EventContentDownloadInfo = {
    total_files: number
    teams: number
    cached: boolean
    filename: string
    download_url?: string
}

export class NoEventContentError extends Error {
    constructor() {
        super("NO_EVENT_CONTENT")
        this.name = "NoEventContentError"
    }
}

export const isNoEventContentError = (error: unknown): error is NoEventContentError =>
    error instanceof NoEventContentError

type Event = {
    id: number
    name: string
    api_key: string
    activity: {code: string, name: string, id: number}
    activity_name: string
    date: Date | string
    comments: string
    logo: string | null
    finished: boolean
    demo: boolean
}

// This will be set by the component that uses this dataFilter
let demoFilter: boolean | null = null

export const setDemoFilter = (demo: boolean | null) => {
    demoFilter = demo
}

const fetch_content = async (params?: { dateRange?: [Date | null, Date | null] | null; filters?: any }) => {
    const storeEvents = useEventsStore()
    await storeEvents.getEvents(params)
}

const filter_gateway = (dataFilter: any) => {
    const storeEvents = useEventsStore()
    let allData: Event[] = []
    
    // Transform events from store to Event format
    storeEvents.events.forEach((e: any) => {
        const ev: Event = {
            id: e.id,
            name: e.name,
            api_key: e.api_key,
            activity: e.activity,
            activity_name: e.activity?.name || '',
            date: e.date,
            comments: e.comments,
            logo: e.logo,
            finished: e.finished,
            demo: e.demo
        }
        allData.push(ev)
    })
    
    // Apply demo filter if set
    if (demoFilter !== null) {
        allData = allData.filter(item => item.demo === demoFilter)
    }
    
    // Apply search filter if provided
    if (dataFilter?.search && dataFilter.search !== "") {
        const searchLower = dataFilter.search.toLowerCase()
        allData = allData.filter(item => 
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.activity_name && item.activity_name.toLowerCase().includes(searchLower))
        )
    }
    
    return allData
}

const create_table_cols = () => {
    const { t } = i18n.global
    return [
        {
            title: "#",
            key: "id",
            sorter: (a: Event, b: Event) => a.id - b.id,
            defaultSortOrder: "descend" as const
        },
        {
            title: t('events.event'),
            key: "name"
        },
        {
            title: t('events.game'),
            key: "activity_name"
        },
        {
            title: t('events.day'),
            key: "date"
        }
    ]
}

const create_filter_object = async () => {
    const { t } = i18n.global
    return [
        {
            id: "search",
            label: t('events.search') || 'Buscar',
            type: "search",
            placeholder: t('events.search_name') || 'Buscar por nombre...'
        }
    ]
}

const deleteEvent = async (id: any) => {
    const storeEvents = useEventsStore()
    const { t } = i18n.global
    
    // Find the event by id to get api_key
    const event: any = storeEvents.events.find((e: any) => e.id === id)
    if (!event) {
        throw new Error('Event not found')
    }
    
    await axiosToken("delete", "events/event/" + event.api_key, {})
        .then(async (response: AxiosResponse) => {
            if (response.status === 202) {
                storeEvents.clearEventSelected()
                await storeEvents.getEvents()
            } else {
                throw new Error(t('messages.error_deleting_event'))
            }
        })
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Created synchronously on user click so long ZIP builds can still auto-download. */
let exportDownloadIframe: HTMLIFrameElement | null = null

const createExportDownloadIframe = () => {
    removeExportDownloadIframe()
    const iframe = document.createElement("iframe")
    iframe.style.display = "none"
    iframe.setAttribute("aria-hidden", "true")
    document.body.appendChild(iframe)
    exportDownloadIframe = iframe
}

const removeExportDownloadIframe = () => {
    if (exportDownloadIframe?.parentNode) {
        exportDownloadIframe.parentNode.removeChild(exportDownloadIframe)
    }
    exportDownloadIframe = null
}

const triggerS3Download = (downloadUrl: string, filename: string) => {
    if (exportDownloadIframe?.parentNode) {
        exportDownloadIframe.src = downloadUrl
        return
    }

    const link = document.createElement("a")
    link.href = downloadUrl
    link.setAttribute("download", filename)
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const resolveExportDownloadUrl = async (
    apiKey: string,
    readyProgress?: EventContentDownloadProgress
) => {
    if (readyProgress?.download_url) {
        return readyProgress.download_url
    }

    const linkResponse = await axiosToken("get", `events/download_images/${apiKey}`)
    if (
        linkResponse?.status === 200 &&
        linkResponse.data?.download_url
    ) {
        return linkResponse.data.download_url as string
    }

    throw new NoEventContentError()
}

/** Same download trigger for cached ZIP and freshly uploaded ZIP. */
const completeExportDownload = async (
    apiKey: string,
    downloadUrl: string,
    filename: string,
    readyProgress?: EventContentDownloadProgress
) => {
    const storeEvents = useEventsStore()
    const resolvedUrl = downloadUrl || await resolveExportDownloadUrl(apiKey, readyProgress)

    storeEvents.setEventContentExportDownload(resolvedUrl, filename)
    storeEvents.updateEventContentExportProgress({
        status: "transferring",
        download_url: resolvedUrl,
    })

    triggerS3Download(resolvedUrl, filename)
    await sleep(1000)
    storeEvents.finishEventContentExport()
}

const waitAndDownloadExport = async (apiKey: string, filename: string) => {
    for (let attempt = 0; attempt < 7200; attempt++) {
        const progressResponse = await axiosToken(
            "get",
            `events/download_images/${apiKey}/progress`
        )
        if (progressResponse?.data) {
            const progress = progressResponse.data as EventContentDownloadProgress
            const storeEvents = useEventsStore()
            storeEvents.updateEventContentExportProgress(progress)

            if (progress.status === "ready") {
                await completeExportDownload(
                    apiKey,
                    progress.download_url || "",
                    filename,
                    progress
                )
                return
            }
            if (progress.status === "error") {
                throw new NoEventContentError()
            }
        }
        await sleep(500)
    }
    throw new NoEventContentError()
}

const downloadEvent = async (id: any) => {
    const storeEvents = useEventsStore()

    const event: any = storeEvents.events.find((e: any) => e.id === id)
    if (!event) {
        throw new Error('Event not found')
    }

    createExportDownloadIframe()

    try {
        const infoResponse = await axiosToken(
            "get",
            `events/download_images/${event.api_key}/info`
        )
        if (!infoResponse || infoResponse.status === 204 || !infoResponse.data?.total_files) {
            throw new NoEventContentError()
        }

        const info = infoResponse.data as EventContentDownloadInfo
        const filename = info.filename || `${event.name}.zip`
        storeEvents.startEventContentExport(info.total_files)

        if (info.cached && info.download_url) {
            storeEvents.updateEventContentExportProgress({
                total_files: info.total_files,
                processed_files: info.total_files,
                remaining_files: 0,
                status: "ready",
                download_url: info.download_url,
            })
            await completeExportDownload(event.api_key, info.download_url, filename)
            return
        }

        await waitAndDownloadExport(event.api_key, filename)
    } catch (error) {
        storeEvents.finishEventContentExport()
        throw error
    } finally {
        window.setTimeout(removeExportDownloadIframe, 120000)
    }
}

const load_row_props = (row: Event) => {
    return {
        onClick: () => {
            try {
                const router = useRouter()
                if (router) {
                    router.push("/events/dashboard/" + row.api_key)
                } else {
                    // Fallback to window location if router is not available
                    window.location.href = "/events/dashboard/" + row.api_key
                }
            } catch (error) {
                // Fallback to window location if router is not available
                window.location.href = "/events/dashboard/" + row.api_key
            }
        }
    }
}

export const eventsList = {
    fetch_content,
    create_table_cols,
    create_filter_object,
    filter_gateway,
    deleteEvent,
    downloadEvent,
    rowClickUrl: (row: Event) => "/events/dashboard/" + row.api_key,
    url_edit: "/events/",
    load_row_props
}

